import { OverQuotaNotice } from "@components/OverQuotaNotice";
import { createNucleotideSequence } from "@db/createNucleotideSequence";
import { groupAsCollection } from "@db/groupAsCollection";
import {
  type ParsedSequence,
  parseSequenceInput,
} from "@formats/sequence-input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCurrentUser } from "@hooks/use-current-user";
import { useStorageQuota } from "@hooks/use-storage-quota";
import { formatBp } from "@lib/genetics";
import { Button } from "@ui/button";
import { DialogFooter } from "@ui/dialog";
import { FieldError } from "@ui/field";
import { Input } from "@ui/input";
import { Label } from "@ui/label";
import { ScrollArea } from "@ui/scroll-area";
import { Switch } from "@ui/switch";
import { Textarea } from "@ui/textarea";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const fastaFormSchema = z.object({
  input: z.string().min(1, "Paste a FASTA sequence"),
  collectionName: z.string(),
});

type FastaFormData = z.infer<typeof fastaFormSchema>;

type FastaFormProps = {
  onClose: () => void;
};

export function FastaForm({ onClose }: FastaFormProps) {
  const { register, handleSubmit, watch, setValue } = useForm<FastaFormData>({
    resolver: zodResolver(fastaFormSchema),
    defaultValues: { input: "", collectionName: "" },
  });

  const [asCollection, setAsCollection] = useState(false);
  const { user } = useCurrentUser();
  const { isOverQuota } = useStorageQuota();
  const ownerId = user?.id ?? "local";

  const input = watch("input");
  const collectionName = watch("collectionName");

  const parseResult = useMemo(() => parseSequenceInput(input, ""), [input]);

  const hasErrors = parseResult.errors.length > 0;
  const hasSequences = parseResult.sequences.length > 0;
  const hasMultiple = parseResult.sequences.length > 1;
  const canSubmit = hasSequences && !hasErrors && !isOverQuota;

  async function onSubmit(data: FastaFormData) {
    const result = parseSequenceInput(data.input, "");
    if (result.errors.length > 0 || result.sequences.length === 0) return;

    const { sequences } = result;

    const results = await Promise.all(
      sequences.map((seq) =>
        createNucleotideSequence({
          name: seq.name,
          sequence: seq.sequence,
          ownerId,
          topology: "linear",
        }),
      ),
    );

    if (asCollection && hasMultiple) {
      const name = data.collectionName.trim() || sequences[0].name;
      await groupAsCollection(results, name, ownerId);
    }

    onClose();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex h-80 flex-col gap-2 py-4">
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
          <div className="flex min-h-0 flex-col gap-2">
            {asCollection && hasMultiple && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fasta-collection-name">Collection Name</Label>
                <Input
                  id="fasta-collection-name"
                  {...register("collectionName")}
                />
              </div>
            )}
            <Textarea
              {...register("input")}
              placeholder={">Header\nACGTATTGGACACACATATATAT"}
              className="min-h-0 flex-1 resize-none break-all font-mono"
            />
          </div>
          <SequenceList sequences={parseResult.sequences} />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground text-sm">
              {isOverQuota ? (
                <OverQuotaNotice />
              ) : parseResult.sequences.length === 0 ? (
                "\u00A0"
              ) : (
                `${parseResult.sequences.length} sequence${parseResult.sequences.length > 1 ? "s" : ""}`
              )}
            </div>
            {hasMultiple && (
              <div className="flex items-center gap-2">
                <Label htmlFor="fasta-collection-toggle">
                  Create as collection
                </Label>
                <Switch
                  id="fasta-collection-toggle"
                  checked={asCollection}
                  onCheckedChange={(checked) => {
                    setAsCollection(checked);
                    if (checked && !collectionName) {
                      setValue(
                        "collectionName",
                        parseResult.sequences[0]?.name ?? "",
                      );
                    }
                  }}
                />
              </div>
            )}
          </div>

          {parseResult.errors.length > 0 && (
            <FieldError>
              {parseResult.errors.map((error) => (
                <div key={error.line}>
                  Line {error.line}: {error.message}
                </div>
              ))}
            </FieldError>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {asCollection && hasMultiple ? "Create Collection" : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
}

type SequenceListProps = {
  sequences: ParsedSequence[];
};

function SequenceList({ sequences }: SequenceListProps) {
  const isEmpty = sequences.length === 0;

  return (
    <div className="h-full overflow-hidden rounded-md border">
      {isEmpty ? (
        <div className="flex h-full items-center justify-center bg-muted text-muted-foreground text-sm">
          Preview
        </div>
      ) : (
        <ScrollArea className="h-full">
          <div className="divide-y">
            {sequences.map((seq) => (
              <div
                key={seq.id}
                className="flex justify-between px-3 py-2 text-sm"
              >
                <span className="font-medium">{seq.name}</span>
                <span className="text-muted-foreground">
                  {formatBp(seq.sequence.length)}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
